import { DEFAULT_BRAND, tenantInsights, type TenantTarget } from '@cyberscore/contracts';
import {
  Bot,
  Briefcase,
  Building2,
  CheckCircle2,
  FileSignature,
  KeyRound,
  LayoutGrid,
  Mail,
  Palette,
  Send,
  Undo2,
  Users,
  Webhook,
  Workflow,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { href } from '../../lib/router';
import { useSession } from '../../lib/session';
import {
  ActivityList,
  FigureStrip,
  InsightRow,
  NextAction,
  RecordBody,
  RecordColumns,
  RecordFrame,
  RecordHero,
  useRecordTab,
  type ActivityItem,
  type Figure,
  type FolderTab,
  type Insight,
} from '../../ui/record/Record';
import { BrandingPage } from '../branding/BrandingPage';
import { ClientFieldsPage } from '../clients/ClientFieldsPage';
import { InsurerFormsPage } from '../insurers/InsurerFormsPage';
import { IntegrationsPage } from '../integrations/IntegrationsPage';
import { NoticesPage } from '../messages/NoticesPage';
import { OrganisationsPage } from '../people/OrganisationsPage';
import { PeoplePage } from '../people/PeoplePage';
import { RolesPage } from '../people/RolesPage';
import { ProvidersPage } from '../providers/ProvidersPage';
import { StudioPage, when } from '../studio/StudioPage';
import type { Release } from '../studio/api';
import { useSettingsAccess, type SettingsAccess } from './access';
import { useTenantSignals } from './api';

type Tab =
  | 'overview'
  | 'journeys'
  | 'insurer-forms'
  | 'people'
  | 'branding'
  | 'notices'
  | 'integrations'
  | 'providers'
  | 'organisations';

const ALL_TABS: (FolderTab & { id: Tab; sees: (a: SettingsAccess) => boolean; blurb: string })[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid, sees: () => true, blurb: '' },
  { id: 'journeys', label: 'Journeys', icon: Workflow, sees: (a) => a.studio, blurb: 'Journeys, questions, controls and scoring, released through approval.' }, // prettier-ignore
  { id: 'insurer-forms', label: 'Insurer forms', icon: FileSignature, sees: (a) => a.insurerForms, blurb: 'The insurers’ proposal forms, and the blank PDFs they are printed on.' }, // prettier-ignore
  { id: 'people', label: 'People and roles', icon: Users, sees: (a) => a.people || a.roles, blurb: 'Who works here, their roles, and what each role may do.' }, // prettier-ignore
  { id: 'branding', label: 'Branding', icon: Palette, sees: (a) => a.branding, blurb: 'The colour your people see across CyberScore.' }, // prettier-ignore
  { id: 'notices', label: 'Notices and fields', icon: Mail, sees: (a) => a.notices || a.clientFields, blurb: 'The wording of your email notices, and what else you keep on each client.' }, // prettier-ignore
  { id: 'integrations', label: 'Integrations', icon: Webhook, sees: (a) => a.integrations, blurb: 'API keys and webhooks for your own systems.' }, // prettier-ignore
  { id: 'providers', label: 'AI providers', icon: Bot, sees: (a) => a.providers, blurb: 'The models the assistant and the studio use.' }, // prettier-ignore
  { id: 'organisations', label: 'Organisations', icon: Building2, sees: (a) => a.organisations, blurb: 'The insurers and brokers on the platform.' }, // prettier-ignore
];

const TARGET_TAB: Record<TenantTarget, string> = {
  journeys: href.tenant('journeys'),
  releases: href.releases(),
  insurer_forms: href.tenant('insurer-forms'),
  people: href.tenant('people'),
  integrations: href.tenant('integrations'),
};
const CHECK_ICON: Record<TenantTarget, LucideIcon> = {
  journeys: Workflow,
  releases: Send,
  insurer_forms: FileSignature,
  people: Users,
  integrations: KeyRound,
};
const count = (n: number | undefined, one: string, many = `${one}s`) =>
  n === undefined ? '' : `${n} ${n === 1 ? one : many}`;
const KIND: Record<string, string> = { platform: 'Platform', insurer: 'Insurer', broker: 'Broker' };

/**
 * Tenant 360 (task 102, decision 0073): the organisation is the record. The header says what in its
 * configuration needs looking at (checks worked out from the routes the reader can already call); the
 * tabs are its settings, each the page it always was.
 */
export function Tenant360() {
  const { tenant } = useSession();
  const access = useSettingsAccess();
  const tabs = ALL_TABS.filter((t) => t.sees(access));
  const { active, select } = useRecordTab(tabs);
  const { signals, figures, releases } = useTenantSignals(access);
  if (!tenant) return null;

  const checks = tenantInsights(signals);
  const insights: Insight[] = checks.map((c) => ({
    id: c.id,
    icon: CHECK_ICON[c.action.target],
    title: c.title,
    text: c.text,
    tone: c.tone,
    source: { label: c.source.label, href: TARGET_TAB[c.source.target] },
    action: { label: c.action.label, href: TARGET_TAB[c.action.target] },
  }));
  const kind = KIND[tenant.kind] ?? tenant.kind;
  const colour = tenant.brand ?? DEFAULT_BRAND.staff;

  return (
    <RecordFrame>
      <RecordHero
        crumbs={[{ label: 'Settings' }]}
        title={tenant.name}
        pill={<span className="pill neutral">{kind}</span>}
        subline={[
          `${kind} organisation`,
          ...(figures.people !== undefined ? [count(figures.people, 'person', 'people')] : []),
          <span key="c" className="t360-colour">
            <i className="t360-swatch" style={{ background: colour }} aria-hidden="true" />
            {tenant.brand ? colour : 'Default colour'}
          </span>,
        ]}
        tabs={tabs}
        active={active}
        onSelect={(id) => select(id as Tab)}
        tabsLabel="Settings"
      >
        <InsightRow
          title="Configuration checks"
          note="What would give a wrong score, a form that can’t be sent, or access left open."
          insights={insights}
          empty="All clear: nothing in your configuration needs looking at."
        />
      </RecordHero>
      <RecordBody>
        {active === 'overview' && (
          <Overview
            tabs={tabs.slice(1)}
            figures={figures}
            colour={tenant.brand}
            onOpen={select}
            next={checks[0]}
            releases={releases}
            studio={access.studio}
          />
        )}
        {active === 'journeys' && <StudioPage embedded />}
        {active === 'insurer-forms' && <InsurerFormsPage embedded />}
        {active === 'people' && (
          <>
            {access.people && <PeoplePage embedded />}
            {access.roles && <RolesPage embedded />}
          </>
        )}
        {active === 'branding' && <BrandingPage embedded />}
        {active === 'notices' && (
          <>
            {access.notices && <NoticesPage embedded />}
            {access.clientFields && <ClientFieldsPage embedded />}
          </>
        )}
        {active === 'integrations' && <IntegrationsPage embedded />}
        {active === 'providers' && <ProvidersPage embedded />}
        {active === 'organisations' && <OrganisationsPage embedded />}
      </RecordBody>
    </RecordFrame>
  );
}

function Overview({
  tabs,
  figures: f,
  colour,
  onOpen,
  next,
  releases,
  studio,
}: {
  tabs: (FolderTab & { id: Tab; blurb: string })[];
  figures: ReturnType<typeof useTenantSignals>['figures'];
  colour: string | null;
  onOpen: (id: Tab) => void;
  next: ReturnType<typeof tenantInsights>[number] | undefined;
  releases: Release[];
  studio: boolean;
}) {
  const figures: Figure[] = [
    ...(f.clients !== undefined ? [{ icon: Briefcase, label: 'Active clients', value: f.clients }] : []),
    ...(f.journeys !== undefined ? [{ icon: Workflow, label: 'Journeys', value: f.journeys, extra: f.questions !== undefined ? `${f.questions} questions` : undefined }] : []), // prettier-ignore
    ...(f.forms ? [{ icon: FileSignature, label: 'Insurer forms ready', value: f.forms.ready, extra: `of ${f.forms.total}` }] : []), // prettier-ignore
    ...(f.people !== undefined ? [{ icon: Users, label: 'People', value: f.people, extra: f.invitations ? `${f.invitations} invited` : undefined }] : []), // prettier-ignore
  ];
  const state: Partial<Record<Tab, string>> = {
    journeys: [count(f.journeys, 'journey'), count(f.questions, 'question'), count(f.controls, 'control')]
      .filter(Boolean)
      .join(' · '),
    'insurer-forms': f.forms ? `${f.forms.ready} of ${f.forms.total} ready to print` : '',
    people: [count(f.people, 'person', 'people'), f.invitations ? `${f.invitations} invited` : '']
      .filter(Boolean)
      .join(', '),
    branding: colour ? `Your colour, ${colour}` : 'The default colour',
    integrations: f.keys !== undefined ? `${count(f.keys, 'API key')} in use` : '',
  };
  const activity: ActivityItem[] = releases.slice(0, 8).map((r) => ({
    id: r.id,
    icon: r.status === 'approved' ? CheckCircle2 : r.status === 'rejected' ? XCircle : r.status === 'withdrawn' ? Undo2 : Send, // prettier-ignore
    when: when(r.decided_at ?? r.submitted_at),
    title: `${r.kind === 'rollback' ? 'Rollback' : 'Release'} ${r.status === 'pending' ? 'submitted' : r.status}${r.version_number ? ` · version ${r.version_number}` : ''}`, // prettier-ignore
    text: [r.note, r.status === 'pending' ? r.submitted_by_name : (r.decided_by_name ?? r.submitted_by_name)].filter(Boolean).join(' · '), // prettier-ignore
    href: href.releases(),
  }));
  return (
    <>
      {figures.length > 0 && <FigureStrip figures={figures} />}
      <RecordColumns
        main={
          <section className="card">
            <div className="card-head">
              <div className="card-title">Your settings</div>
            </div>
            <ul className="t360-areas">
              {tabs.map((t) => (
                <li key={t.id}>
                  <span className="t360-area-icon">
                    <t.icon size={17} aria-hidden="true" />
                  </span>
                  <div className="grow">
                    <b>{t.label}</b>
                    <p className="muted small">{state[t.id] || t.blurb}</p>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={() => onOpen(t.id)}>
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </section>
        }
        side={
          <>
            {next ? (
              <NextAction
                title={next.title}
                detail={next.text}
                action={{ label: next.action.label, href: TARGET_TAB[next.action.target] }}
              />
            ) : (
              <NextAction title="Nothing to fix" detail="The configuration checks are all clear." />
            )}
            {studio && (
              <section className="card">
                <div className="card-head">
                  <div className="card-title">Configuration changes</div>
                </div>
                <ActivityList items={activity} empty="No releases yet." />
              </section>
            )}
          </>
        }
      />
    </>
  );
}
